import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { type ColumnDef } from '@tanstack/react-table'
import { PencilIcon, PlusIcon } from 'lucide-react'
import { useMemo } from 'react'

import { Cell, DataTable, useDataTable } from '@/components/block/data-table'
import { Page } from '@/components/layout/page'
import { Button } from '@/components/ui/button'
import { CardExtendTable } from '@/components/ui/card-extend'

import { productApi } from '@/features/product/api/product-api'
import type { ProductSelectDto } from '@/features/product/schema/product-schema'

import { useAuth } from '@/lib/auth'
import { toIdr } from '@/libs/utils/format'

export const Route = createFileRoute('/_app/product/')({
  component: Component,
})

function Component() {
  return (
    <Page>
      <Page.Breadcrumbs />
      <ProductTable />
    </Page>
  )
}

function ProductTable() {
  // const navigate = Route.useNavigate()
  const outletId = useAuth().outlet.oId

  const { isLoading, data } = useQuery(productApi.list.query({ params: { outletId } }))

  const columns = useMemo(getColumns, [])
  const table = useDataTable({
    columns,
    data: data?.data.products ?? [],
    isLoading,
    // onRowClick: row => navigate({ to: '/product/$id', params: { id: row.original.id } }),
  })

  return (
    <CardExtendTable
      title='Daftar Produk'
      action={
        <Button asChild size='sm'>
          <Link to='/product/create'>
            <PlusIcon />
            Tambah Produk
          </Link>
        </Button>
      }
    >
      <DataTable.Root table={table}>
        <DataTable.Table />
        <DataTable.Pagination />
      </DataTable.Root>
    </CardExtendTable>
  )
}

function getColumns(): ColumnDef<ProductSelectDto>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Nama',
      cell: ({ row }) => (
        <Cell.Link from={Route.fullPath} to='/product/$id/update' params={{ id: row.original.id }}>
          {row.original.name}
        </Cell.Link>
      ),
    },
    {
      accessorKey: 'variants',
      id: 'variants',
      header: 'Varian',
      cell: ({ row }) => {
        const { hasVariant, variants } = row.original
        const text = !hasVariant ? '-' : variants.map(x => x.name).join(', ')
        return <Cell.Text text={text} />
      },
    },
    {
      accessorKey: 'recipeId',
      header: 'Resep',
      cell: ({ row }) => {
        const hasRecipe = !!row.original.recipeId

        return (
          <Button asChild variant='ghost' size='icon-sm'>
            <Link
              from={Route.fullPath}
              to='/inventory/recipe'
              search={{ refId: row.original.id, refType: 'product' }}
            >
              {hasRecipe ? <PencilIcon /> : <PlusIcon />}
            </Link>
          </Button>
        )
      },
    },
    {
      accessorKey: 'price',
      header: 'Harga Jual',
      cell: ({ row }) => <Cell.Text text={toIdr(row.original.price)} />,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => <Cell.Date date={row.original.createdAt} />,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated At',
      cell: ({ row }) => <Cell.Date date={row.original.updatedAt} />,
    },
  ]
}
